import { keepPreviousData } from '@tanstack/react-query';

import { queryKeys } from '../../lib/queryKeys';
import type { RequestError } from '../../shared';
import { observableToPromise } from '../../shared/observableToPromise';
import {
  type EnvironmentsPage,
  getAccountProjects,
  getProjectEnvironments,
  type ProjectsPage,
  searchAccountEnvironments,
} from '../api';
import type { Account, Environment, EnvironmentSummary, Project } from '../model';
import { isObjectIdHex } from '../utils';

import { useAccountQuery } from './useAccountQuery';

export const PROJECTS_PAGE_SIZE = 20;
// Name and key matches past this many are not fetched; the panel asks for a narrower search instead.
export const PROJECT_MATCH_LIMIT = 100;
// A one-character term fuzzy-matches nearly every environment in the account.
export const MIN_SEARCH_TERM_LENGTH = 2;

/** A page of projects together with the offset it was fetched at. */
export type ProjectsPageView = ProjectsPage & Readonly<{ offset: number }>;

/**
 * One page of every project in the account, for browsing without a search term.
 * The previous page stays on screen while the next loads; it carries its own
 * offset so the pagination range always describes the projects being shown.
 */
export function useProjectsPage(account: Account, offset: number) {
  return useAccountQuery<ProjectsPageView, RequestError>(
    queryKeys.accounts.projectsPage(account._id, offset),
    async ({ signal }) => ({
      ...(await observableToPromise(getAccountProjects(account, { limit: PROJECTS_PAGE_SIZE, offset }), signal)),
      offset,
    }),
    account,
    { placeholderData: keepPreviousData },
  );
}

export type ProjectSearchResults = Readonly<{
  /**
   * Projects matched by name, key or ID, with all their environments, followed by
   * projects reached only through a matching environment, carrying just those.
   */
  projects: Project[];
  /** More projects matched by name or key than were fetched. */
  truncated: boolean;
  /** Set when one half of the search failed; the other half's results are still shown. */
  failures?: SearchFailures;
}>;

export type SearchFailures = Readonly<{
  projects?: RequestError;
  environments?: RequestError;
}>;

// The environment search is fuzzy subsequence matching over a string that
// includes hex IDs and SDK keys, so a short term matches almost everything.
// Keep the hits where every word of the term is a plain substring, which is
// what the old client-side filter did.
export function environmentMatchesTerm(hit: EnvironmentSummary, term: string): boolean {
  const haystack = [hit._id, hit.key, hit.name, hit.projectKey, hit.projectName, hit.apiKey, hit.mobileKey]
    .join(' ')
    .toLowerCase();
  return term
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => haystack.includes(word));
}

/**
 * Merges the two halves of a search the way the old client-side filter did: a
 * project hit shows every environment, an environment hit shows only the matching
 * environments of its project. Environment hits carry no project ID; the panel
 * fills that in for the projects on screen.
 */
export function mergeProjectSearchResults(
  page: ProjectsPage | undefined,
  environmentHits: readonly EnvironmentSummary[] | undefined,
  term: string,
): ProjectSearchResults {
  const projects = page?.items ?? [];
  const matched = new Set(projects.map((project) => project.key));
  const byProject = new Map<string, { key: string; name: string; environments: Environment[] }>();

  for (const hit of environmentHits ?? []) {
    if (matched.has(hit.projectKey) || !environmentMatchesTerm(hit, term)) {
      continue;
    }
    const { projectKey, projectName, ...environment } = hit;
    const group = byProject.get(projectKey) ?? { key: projectKey, name: projectName, environments: [] };
    group.environments.push(environment);
    byProject.set(projectKey, group);
  }

  const fromEnvironments: Project[] = [...byProject.values()].map((group) => ({
    _id: '',
    key: group.key,
    name: group.name,
    environments: group.environments,
    environmentsTotalCount: group.environments.length,
  }));

  return {
    projects: [...projects, ...fromEnvironments],
    truncated: (page?.totalCount ?? 0) > projects.length,
  };
}

/**
 * Projects and environments matching a search term, across the whole account.
 * Both halves are fetched inside one query so a merged result always comes from
 * a single term; keepPreviousData then holds the last complete result while the
 * next term loads. If one half fails its error is reported alongside the other
 * half's results; only when both fail does the query error.
 */
export function useProjectSearch(account: Account, term: string) {
  const projectParams = isObjectIdHex(term)
    ? { ids: [term.toLowerCase()], limit: PROJECT_MATCH_LIMIT }
    : { query: term, limit: PROJECT_MATCH_LIMIT };

  return useAccountQuery<ProjectSearchResults, RequestError>(
    queryKeys.accounts.projectSearch(account._id, term),
    async ({ signal }) => {
      const [projects, environments] = await Promise.allSettled([
        observableToPromise(getAccountProjects(account, projectParams), signal),
        observableToPromise(searchAccountEnvironments(account, term), signal),
      ]);
      if (projects.status === 'rejected' && environments.status === 'rejected') {
        throw projects.reason;
      }
      return {
        ...mergeProjectSearchResults(settledValue(projects), settledValue(environments), term),
        failures: { projects: settledError(projects), environments: settledError(environments) },
      };
    },
    account,
    { enabled: term.length >= MIN_SEARCH_TERM_LENGTH, placeholderData: keepPreviousData },
  );
}

const settledValue = <T,>(result: PromiseSettledResult<T>) => (result.status === 'fulfilled' ? result.value : undefined);
const settledError = <T,>(result: PromiseSettledResult<T>) =>
  result.status === 'rejected' ? (result.reason as RequestError) : undefined;

/** The named projects, used to look up IDs the environment search does not return. */
export function useProjectsByKeys(account: Account, keys: readonly string[]) {
  return useAccountQuery<ProjectsPage, RequestError>(
    queryKeys.accounts.projectsByKeys(account._id, keys),
    ({ signal }) => observableToPromise(getAccountProjects(account, { keys, limit: keys.length }), signal),
    account,
    { enabled: keys.length > 0, placeholderData: keepPreviousData },
  );
}

/** Every environment of one project, for the ones with more than the 20 the projects list inlines. */
export function useProjectEnvironments(account: Account, projectKey: string, enabled = true) {
  return useAccountQuery<EnvironmentsPage, RequestError>(
    queryKeys.accounts.projectEnvironments(account._id, projectKey),
    ({ signal }) => observableToPromise(getProjectEnvironments(account, projectKey), signal),
    account,
    { enabled, staleTime: 60_000, retry: false },
  );
}
